<!-- Componente che realizza tramite Chart.js un grafico a linee con il tempo nell'asse delle ascisse e la presenza rete nell'asse delle ordinate.
     Da ReportMains.vue riceve un dataset per ogni Centrale dell'impianto corrente, e lo rappresenta nel grafico con un colore distinto-->
<script>

import { Line } from 'vue-chartjs';
import 'chartjs-plugin-zoom'; //in v2 basta importare per registrare globalmente
import { Tooltip } from 'bootstrap';


export default {
  name: 'MainsStepLineChart',
  extends: Line,
  data() {
    return {
      chartOptions: {
        devicePixelRatio: 2, //raddoppia la densità dei pixel (ottimo per la stampa)
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
						ticks: { beginAtZero: true, stepSize: 1, max: 1.5, min: 0, display: false },
						gridLines: { display: false	},
					}],
          xAxes: [{
            type: 'time',
            distribution: 'linear', //scala temporale reale
            bounds: 'ticks', //mantiene i bordi puliti sui ticks
            time: {
              parser: 'MM/DD/YYYY, h:mm:ss A',
              //unit: 'minute', //occorre lasciare che Chart.js scali da solo
              tooltipFormat: 'DD/MM/YYYY, hh:mm:ss',
              displayFormats: {
                second: 'HH:mm:ss',
                minute: 'DD/MM HH:mm',
                hour: 'DD/MM HH:mm',
                day: 'DD/MM',
                month: 'MM/YYYY',
                year: 'YYYY'
              }
            },
            ticks: {
              min: this.startTimeMs,
              max: this.endTimeMs,
              autoSkip: true, //salta le etichette per evitare sovrapposizioni
              display: true,
              source: 'auto', //calcola la scala in base al tempo, non solo ai punti
              maxTicksLimit: 20 //forza un massimo di 20 etichette totali
            },
            gridLines: { display: true },
          }]
        },
        //CONFIGURAZIONE ZOOM PER CHART.JS 2.x
        pan: {
          enabled: true,
          mode: 'x',
          speed: 10,
          threshold: 10,
          rangeMin: { x: this.startTimeMs }, //blocca l'inizio (non si può superare questo limite)
          rangeMax: { x: this.endTimeMs }, //blocca la fine (non si può superare questo limite)
          onPan: (chart) => {
            this.emitBounds(chart);
          }
        },
        zoom: {
          enabled: true,
          mode: 'x',
          speed: 0.05,
          sensitivity: 0.5,
          //limiti alla zoom out
          rangeMin: { x: this.startTimeMs }, //blocca l'inizio (non si può superare questo limite)
          rangeMax: { x: this.endTimeMs }, //blocca la fine (non si può superare questo limite)
          onZoom: (chart) => {
            this.emitBounds(chart);
          }
        },
        onHover: (event, chartElement) => {
          event.target.style.cursor = chartElement.length ? 'pointer' : '';
        },
        legend: {
          display: true,
          onHover: (event, legendItem) => {
            //1. cambia il cursore
            event.target.style.cursor = 'pointer';
            //2. evidenzia il dataset corrispondente
            const chart = this.$data._chart;
            const index = legendItem.datasetIndex;
            chart.data.datasets.forEach((dataset, i) => {
              //se è il dataset puntato, lo rendiamo marcato e sbiadiamo i restanti
              dataset.borderWidth = (i === index) ? 4 : 1;
            });
            chart.update(0); //aggiorna senza animazione
          },
          onLeave: (event) => {
            event.target.style.cursor = ''; // torna al CSS 'grab'
            //3. ripristina lo stato originale di tutti i dataset
            const chart = this.$data._chart;
            chart.data.datasets.forEach((dataset) => {
              dataset.borderWidth = 2; //lo spessore standard
            });
            chart.update(0); //aggiorna senza animazione
          },
          labels: {
            usePointStyle: true,
            padding: 20,
            fontSize: 14
          }
        },
        tooltips: {
          enabled: true,
          mode: 'nearest',       // Prende il punto più vicino al cursore
          intersect: true,      // Permette di attivare il tooltip anche se non sei esattamente sul pixel
          axis: 'x',             // Cerca il punto più vicino lungo l'asse orizzontale
          position: 'nearest',   // Posiziona il fumetto vicino al punto trovato
          animationDuration: 100, // Rende il movimento fluido ma veloce
          callbacks: {
            label: (tooltipItem, data) => {
              //mostra il nome della centrale e la presenza rete
              const datasetLabel = data.datasets[tooltipItem.datasetIndex].label;
              const presenzaRete = (tooltipItem.yLabel === 1) ? 'ON' : 'OFF';
              return `${datasetLabel}: ${presenzaRete} `;
            }
          },
        },
        hover: {
          mode: 'nearest',
          intersect: true,
          animationDuration: 0 //rende il feedback dell'hover istantaneo
        },
        events: ['click', 'mousemove', 'mouseout', 'touchstart', 'touchmove'], //eventi supportati ridotti per velocizzare
      }
    };
  },
  props: {
    chartData: { type: Object, required: true },
    startTimeMs: { type: Number, default: null},
    endTimeMs: { type: Number, default: null },
  },
  emit: ['update-bounds'],
  mounted() {
    console.log('MainsStepLineChart mounted with data:', this.chartData.datasets, ' with these labels: ', this.chartData.labels);
    this.renderChart(this.chartDataComputed, this.chartOptions);
  },
  computed: {
    chartDataComputed() {
      return {
        labels: this.chartData.labels,
        datasets: this.chartData.datasets.map(dataset => ({ //restituisce un nuovo oggetto (shallow clone, nuovo riferimento in memoria), 
          ...dataset,
          stepped: 'after',
          tension: 0,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
        }))
      }
    }
  },
  watch: {
    //quando cambiano i dataset, ri-renderizziamo il grafico
    chartDataComputed(newVal) {
      const start = Number(this.startTimeMs);
      const end = Number(this.endTimeMs);

      if (isNaN(start) || isNaN(end)) return;
      
      //Aggiorniamo i limiti delle scale prima di renderizzare
      this.chartOptions.scales.xAxes[0].ticks.min = start;
      this.chartOptions.scales.xAxes[0].ticks.max = end;
      //aggiorniamo anche i limiti del plugin zoom/pan
      this.chartOptions.pan.rangeMin.x = start;
      this.chartOptions.pan.rangeMax.x = end;
      this.chartOptions.zoom.rangeMin.x = 60 * 1000; //1 minuto
      this.chartOptions.zoom.rangeMax.x = (end - start);

      this.renderChart(newVal, this.chartOptions);
    }
  },

  methods: {
    //funzione che invia al genitore i nuovi bounds temporali
    emitBounds(chartWrapper) {
      //dal wrapper 'chartWrapper' recupero l'istanza di chart
      const chartInstance = chartWrapper.chart || chartWrapper;
      //console.log('istanza reale del grafico:', chartInstance);
      
      if (chartInstance && chartInstance.scales) {
        const xAxis = chartInstance.scales['x-axis-0'];

        if (xAxis) {
          //per sicurezza non mandiamo bounds al di fuori dei limiti iniziali
          const min = Math.max(xAxis.min, this.startTimeMs);
          const max = Math.min(xAxis.max, this.endTimeMs);

          if (!isNaN(min) && !isNaN(max)) {
            this.$emit('update-bounds', { min, max });
          }
        }
      }
    },
    //Funzione che restituisce il grafico (può essere invocata dal componente padre)
    getChartImage() {
      try {
        return this.$data._chart.canvas.toDataURL('image/png');
      } catch (e) {
        console.error('Error generating chart image:', e);
        return null;
      }
    },
    //Reset dello zoom
    resetZoom() {
      if (this.$data._chart) {
        this.$data._chart.resetZoom();
      }
    }
  }
};
</script>
<style scoped>
  canvas {
    cursor: grab;
  }
  canvas:active {
    cursor: grabbing;
  }
</style>